import { h, Fragment } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";

import {
    Variable,
    VariableValue
} from "../../foundation/api/model/variables/Variables";
import Color from "../../foundation/model/color";
import CSSProperties from "../../foundation/model/CSSProperties";

import classname from "../../foundation/utils/classname";
import { copyContent } from "../../foundation/utils/platform_ts";

import ColorBlock from "../v2/ColorBlock";
import IconArrowLeft from "../v2/Icons/IconArrowLeft";
import IconVariable from "../v2/Icons/IconVariable";
import OnlyIconButton from "../v2/OnlyIconButton";
import Popover, { PopoverMode, PopoverPosition } from "../v2/Popover";
import Portal from "../Portal";

import { Props } from "./VariableContainerProps";

import styles from "./VariableContainer.module.css";

const INITIAL_BIG_WIDTH = 9999;
const CONTAINER_OFFSET = 38;
interface VariableProps {
    variable: Variable;
    active?: boolean;
    value?: number | string;
    unit?: string;
    iconVisible?: boolean;
    direction: "rtl" | "ltr";
    expandOnHover?: boolean;
    showValueOnNextLine?: boolean;
    onClick?: (variable: Variable) => void;
    onIconClick?: () => void;
    refCallback?: (el: HTMLButtonElement | null) => void;
}

const Variable = ({
    variable,
    value,
    active,
    onClick,
    refCallback,
    iconVisible,
    direction,
    expandOnHover,
    showValueOnNextLine,
    onIconClick
}: VariableProps) => {
    const [valueVisible, setValueVisible] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const variableContainerRef = useRef<HTMLDivElement | null>(null);
    const iconContainerRef = useRef<HTMLButtonElement | null>(null);
    const valueWidth = useRef(INITIAL_BIG_WIDTH);

    function handleClickVariable(event: MouseEvent) {
        event.stopPropagation();
        onClick?.(variable);
    }

    function handleClickIcon() {
        onIconClick?.();
    }

    function handleMouseEnter() {
        if (!expandOnHover) {
            return;
        }

        setExpanded(true);
    }

    function handleMouseLeave() {
        setExpanded(false);
    }

    function checkEllipsis(variableNameValue: HTMLDivElement | null) {
        if (!variableNameValue || !variableContainerRef.current) {
            return;
        }

        const variableName = variableNameValue.children[0];
        const variableValue = variableNameValue.children[1];

        if (variableValue) {
            valueWidth.current = variableValue.clientWidth;
        }

        const isVariableNameEllipsis = variableName.clientWidth < variableName.scrollWidth;

        if (isVariableNameEllipsis) {
            setValueVisible(false);
        } else if ((variableName.clientWidth + valueWidth.current) <
            (variableContainerRef.current.clientWidth - CONTAINER_OFFSET)) {
            setValueVisible(true);
        }
    }

    function handleIconRef(iconContainer: HTMLButtonElement | null) {
        refCallback?.(iconContainer);
        iconContainerRef.current = iconContainer;
    }

    function getExpandedStyle(): CSSProperties {
        const variableContainerClientRect = variableContainerRef.current!.getBoundingClientRect();
        let right = window.innerWidth - variableContainerClientRect.right;

        if (iconVisible) {
            const iconContainerClientRect = iconContainerRef.current!.getBoundingClientRect();
            right = window.innerWidth - (iconContainerClientRect.left - 4);
        }

        return {
            right,
            top: variableContainerClientRect.top
        };
    }

    function renderExpandedVariable() {
        return (
            <>
                {
                    expanded && !valueVisible && (
                        <Portal>
                            <div
                                class={classname("body-1", styles.variableNameValue, styles.expandedVariable)}
                                style={getExpandedStyle()}>
                                <span class={styles.variableText}>{variable.name}</span>
                                {!showValueOnNextLine && value && (
                                    <span class={classname(styles.variableText, styles.variableValue)}>
                                    &nbsp;({value})
                                    </span>
                                )}
                            </div>
                        </Portal>
                    )
                }
            </>
        );
    }

    return (
        <div ref={variableContainerRef} class={styles.variableContainer}>
            {renderExpandedVariable()}
            <div class={styles.valueContainer}>
                <div class={styles.variableNameContainer}>
                    <div ref={checkEllipsis}
                        class={classname("body-1", styles.variableNameValue, "ellipsis", {
                            [styles.withValue]: iconVisible
                        })}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}>
                        <div
                            class={classname(styles.variableText, styles[direction], "ellipsis")}
                            onClick={handleClickVariable}>
                            {variable.name}
                        </div>
                        {!showValueOnNextLine && value && valueVisible &&
                            <div class={classname(styles.variableText, styles.variableValue)}>&nbsp;({value})</div>}
                    </div>
                </div>
                {showValueOnNextLine && value &&
                    <div class={classname(styles.variableText, styles.variableValue, styles.onNextLine)}>{value}</div>
                }
            </div>
            {iconVisible && (
                <OnlyIconButton
                    buttonRef={handleIconRef}
                    class={classname(styles.iconContainer, styles.variableNameValue, {
                        [styles.activeIcon]: !!active
                    })}
                    icon={IconVariable}
                    activated={!!active}
                    onClick={handleClickIcon}/>
            )}
        </div>
    );
};

export default function VariableContainer({
    platform,
    variable,
    value,
    unit,
    variableModeIdMap,
    mappedVariables,
    toastContainer,
    direction = "rtl",
    expandOnHover = true,
    showValueOnNextLine = false
}: Props) {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [variableStack, setVariableStack] = useState<Variable[]>([variable]);
    const [isVariableNameLong, setIsVariableNameLong] = useState(false);
    const iconRef = useRef<HTMLButtonElement | null>(null);
    const popoverContentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setVariableStack([variable]);
    }, [variable]);

    function trackVariableViewed(isNested = false) {
        mixpanel.master.track("Viewed variable", {
            Type: variable.propertyName,
            isNested
        });
    }

    function trackCopiedVariableName(source: "popover" | "button") {
        mixpanel.master.track("Copied variable name", {
            Type: variable.propertyName,
            Source: source
        });
    }

    function openPopover() {
        setPopoverOpen(true);
        trackVariableViewed();
    }

    function closePopover() {
        setPopoverOpen(false);
        setVariableStack([variable]);
    }

    function pushVariableToStack(variableToAdd: Variable) {
        setVariableStack([...variableStack, variableToAdd]);
        trackVariableViewed(true);
    }

    function popVariableFromStack() {
        const newStack = [...variableStack];
        newStack.pop();
        setVariableStack(newStack);
    }

    function handleRef(el: HTMLButtonElement | null) {
        iconRef.current = el;
    }

    function createHandleCopyVariableName(source: "popover" | "button") {
        return () => {
            copyContent({
                content: variable.name,
                toastContainer: toastContainer || document.querySelector("[data-selector=sidebarForVariables]"),
                successText: "Copied to clipboard"
            });

            trackCopiedVariableName(source);
        };
    }

    function getModeName(variableValue: VariableValue) {
        let { modeName } = variableValue;

        if (variableModeIdMap) {
            modeName = variableModeIdMap[variableValue.modeId].name;
        }

        return modeName;
    }

    function getVariableValue(variableValue: VariableValue) {
        if (!variableValue?.type) {
            return null;
        }

        switch (variableValue.type) {
            case "variable": {
                const referencedVariable = mappedVariables[variableValue.variableSourceId!];
                return (
                    <Variable
                        variable={referencedVariable}
                        direction={direction}
                        expandOnHover={expandOnHover}
                        onClick={pushVariableToStack}/>
                );
            }

            case "color": {
                const colorModel = new Color(variableValue.color!);

                return (
                    <div class={styles.variableColorValue}>
                        <ColorBlock class={styles.colorPreview} color={colorModel.toRGBAString()}/>
                        <div class={styles.variableText}>{colorModel.toFormattedString({ platform })}</div>
                    </div>
                );
            }

            case "number": {
                return <div class={styles.variableText}>{`${variableValue.value}${unit ?? ""}`}</div>;
            }

            case "text": {
                return <div class={styles.variableText}>{variableValue.content}</div>;
            }

            default: return null;
        }
    }

    function checkHeaderEllipsis(header: HTMLSpanElement | null) {
        if (!header) {
            return;
        }

        const isHeaderEllipsis = header.scrollWidth > header.clientWidth;
        setIsVariableNameLong(isHeaderEllipsis);
    }

    function renderPopoverContent() {
        const variableToRender = variableStack[variableStack.length - 1];
        return (
            <div class={classname(
                styles.variablePopoverContentContainer,
                { [styles.withLongName]: isVariableNameLong })}
            ref={popoverContentRef}>
                <div class={styles.variablePopoverHeaderContainer}>
                    {variableStack.length > 1 && (
                        <button class={styles.backButton} onClick={popVariableFromStack}>
                            <IconArrowLeft/>
                        </button>
                    )}
                    <div class={classname(styles.variableHeaderContainer)}
                        onClick={createHandleCopyVariableName("popover")}>
                        <IconVariable/>
                        <span ref={checkHeaderEllipsis} class={classname("body-1", styles.variableHeader, styles.rtl, "ellipsis")}>
                            {variableToRender.name}
                        </span>
                    </div>
                </div>
                <div class={styles.variableValuesContainer}>
                    {variableToRender.values.map(variableValue => (
                        <div key={variableValue.modeId} class={classname("body-1", styles.variableValueContainer)}>
                            <div class={styles.variableValueTitle}>{getModeName(variableValue)}</div>
                            {getVariableValue(variableValue)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <>
            <Variable
                refCallback={handleRef}
                variable={variable}
                value={value}
                active={popoverOpen}
                iconVisible={true}
                unit={unit}
                direction={direction}
                expandOnHover={expandOnHover}
                showValueOnNextLine={showValueOnNextLine}
                onClick={createHandleCopyVariableName("button")}
                onIconClick={openPopover}/>
            <Popover
                class={styles.variablePopover}
                open={popoverOpen}
                mode={PopoverMode.Portal}
                contentClass={styles.variablePopoverContent}
                content={renderPopoverContent()}
                position={PopoverPosition.Right}
                portalOptions={{
                    targetRef: iconRef
                }}
                overlayDivProps={{
                    onClick: (ev: Event) => {
                        ev.stopPropagation();
                        closePopover();
                    }
                }}
                offset={4}
                hasOverlay
                hideArrow
                onClose={closePopover}/>
        </>
    );
}
